import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Lightbulb, Bug, Send, CheckCircle2 } from "lucide-react";
import { cn } from "@/lib/utils";

interface FeedbackModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultType?: "feature" | "bug";
}

export function FeedbackModal({ open, onOpenChange, defaultType = "feature" }: FeedbackModalProps) {
  const [type, setType] = useState<"feature" | "bug">(defaultType);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const { toast } = useToast();
  const { user } = useAuth();

  // The modal stays mounted with its caller, so the initial useState value is
  // only correct for the first open. Re-apply the requested type every time the
  // dialog opens (and if the caller switches it while open) so "Report a bug"
  // never submits as a feature request.
  useEffect(() => {
    if (open) setType(defaultType);
  }, [open, defaultType]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!title.trim() || !description.trim()) {
      toast({
        title: "Missing information",
        description: "Please fill in both the title and description.",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);
    
    try {
      // Save feedback to database
      const { error: dbError } = await supabase
        .from("feedback_submissions")
        .insert({
          user_id: user?.id,
          type,
          title,
          description,
          user_email: user?.email,
        });

      if (dbError) {
        console.error("Error saving feedback to database:", dbError);
        throw dbError;
      }

      // Send feedback email via edge function (non-blocking)
      supabase.functions.invoke("send-feedback-email", {
        body: {
          type,
          title,
          description,
          userEmail: user?.email,
        },
      }).then(({ error }) => {
        if (error) {
          console.error("Error sending feedback email:", error);
        } else {
          console.log("Feedback email sent successfully");
        }
      });

    } catch (err) {
      console.error("Failed to save feedback:", err);
      setIsSubmitting(false);
      toast({
        title: "Something went wrong",
        description: "Failed to submit feedback. Please try again.",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(false);
    setIsSubmitted(true);
    
    // Reset after showing success
    setTimeout(() => {
      setIsSubmitted(false);
      setTitle("");
      setDescription("");
      onOpenChange(false);
    }, 2000);
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setTitle("");
      setDescription("");
      setIsSubmitted(false);
      onOpenChange(false);
    }
  };

  if (isSubmitted) {
    return (
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent className="sm:max-w-md bg-card border-border">
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <div className="rounded-full bg-primary/10 p-4 mb-4">
              <CheckCircle2 className="h-8 w-8 text-primary" />
            </div>
            <h3 className="text-lg font-semibold text-foreground mb-2">
              Thank you! 🎉
            </h3>
            <p className="text-sm text-muted-foreground">
              Your {type === "feature" ? "feature request" : "bug report"} has been submitted.
              We appreciate your feedback!
            </p>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md bg-card border-border">
        <DialogHeader>
          <DialogTitle className="text-foreground">Send Feedback</DialogTitle>
          <DialogDescription>
            Help us improve Fennest by sharing your ideas or reporting issues.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Type Selection */}
          <div className="grid grid-cols-2 gap-3">
            <button
              type="button"
              onClick={() => setType("feature")}
              className={cn(
                "flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all",
                type === "feature"
                  ? "border-primary bg-primary/10"
                  : "border-border bg-background/50 hover:bg-background"
              )}
            >
              <Lightbulb className={cn(
                "h-6 w-6",
                type === "feature" ? "text-primary" : "text-muted-foreground"
              )} />
              <span className={cn(
                "text-sm font-medium",
                type === "feature" ? "text-primary" : "text-muted-foreground"
              )}>
                Feature Request
              </span>
            </button>
            
            <button
              type="button"
              onClick={() => setType("bug")}
              className={cn(
                "flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all",
                type === "bug"
                  ? "border-destructive bg-destructive/10"
                  : "border-border bg-background/50 hover:bg-background"
              )}
            >
              <Bug className={cn(
                "h-6 w-6",
                type === "bug" ? "text-destructive" : "text-muted-foreground"
              )} />
              <span className={cn(
                "text-sm font-medium",
                type === "bug" ? "text-destructive" : "text-muted-foreground"
              )}>
                Report Bug
              </span>
            </button>
          </div>

          {/* Title */}
          <div className="space-y-2">
            <Label htmlFor="feedback-title" className="text-sm">
              {type === "feature" ? "What would you like to see?" : "What went wrong?"}
            </Label>
            <Input
              id="feedback-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={type === "feature" ? "e.g., Add weekly budget view" : "e.g., App crashes when adding expense"}
              className="bg-background border-border/50"
              maxLength={100}
            />
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="feedback-description" className="text-sm">
              Tell us more
            </Label>
            <Textarea
              id="feedback-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={
                type === "feature"
                  ? "Describe the feature you'd like and how it would help you..."
                  : "What were you doing when the bug occurred? Any error messages?"
              }
              className="bg-background border-border/50 min-h-[120px] resize-none"
              maxLength={1000}
            />
            <p className="text-xs text-muted-foreground text-end">
              {description.length}/1000
            </p>
          </div>

          {/* Submit */}
          <Button
            type="submit"
            className="w-full"
            disabled={isSubmitting || !title.trim() || !description.trim()}
          >
            {isSubmitting ? (
              <>
                <span className="animate-spin me-2">⏳</span>
                Submitting...
              </>
            ) : (
              <>
                <Send className="h-4 w-4 me-2" />
                Submit Feedback
              </>
            )}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
